/**
 * The report lifecycle — one axis, four states.
 *
 *     new ──► needs_fix ──► fixed
 *      │          │           │
 *      └──────────┴───────────┴──► dismissed
 *
 * This replaces a design with two orthogonal columns (`status` + `dev_state`)
 * that nothing ever reconciled. The moderator saw up to seven buttons, a row had
 * twelve reachable combinations, and three different controls carried the same
 * Bengali label — so the button that "did not work" was usually a filter pill, or
 * the one control that wrote only the axis the list was not filtered on.
 *
 * The fix is to make a wrong button unrepresentable. The admin card renders
 * `actionsFor(status)` and never names a button itself; buttons resolve their
 * text through `t.admin.actions[labelKey]` while badges and filters resolve
 * through `t.admin.statuses[status]`. Those namespaces are disjoint and a test
 * (`bn.test.ts`) asserts they share no value.
 *
 * Kept separate from `report-types.ts`, which must stay type-only: it is imported
 * by `scripts/pull-reports.ts` under the JSX-less node tsconfig.
 */

/** Must stay in step with the `reports_status_check` constraint in schema.sql. */
export const REPORT_STATUSES = ['new', 'needs_fix', 'fixed', 'dismissed'] as const;
export type ReportStatus = (typeof REPORT_STATUSES)[number];

/** Still needing someone's attention — the moderator queue and the header badge. */
export const ACTIVE_STATUSES = ['new', 'needs_fix'] as const;

export function isActive(status: ReportStatus): boolean {
  return (ACTIVE_STATUSES as readonly string[]).includes(status);
}

/**
 * `new → fixed` is deliberately absent. Routing every fix through `needs_fix`
 * guarantees `npm run reports:pull` sees everything that was ever fixed, and it
 * caps a fresh report at two buttons.
 */
const TRANSITIONS: Record<ReportStatus, readonly ReportStatus[]> = {
  new: ['needs_fix', 'dismissed'],
  needs_fix: ['fixed', 'dismissed'],
  fixed: ['needs_fix', 'dismissed'], // the fix did not hold
  dismissed: ['new'], // undo a mistaken dismissal
};

export function canTransition(from: ReportStatus, to: ReportStatus): boolean {
  return TRANSITIONS[from].includes(to);
}

/** Keys into `t.admin.actions` — imperative verbs, never a state noun. */
export type ActionLabelKey = 'markNeedsFix' | 'markFixed' | 'dismiss' | 'reopen' | 'restore';

export interface ReportAction {
  to: ReportStatus;
  labelKey: ActionLabelKey;
  /** Exactly one `primary` per state; the obvious next move. */
  emphasis: 'primary' | 'quiet';
}

const ACTIONS: Record<ReportStatus, readonly ReportAction[]> = {
  new: [
    { to: 'needs_fix', labelKey: 'markNeedsFix', emphasis: 'primary' },
    { to: 'dismissed', labelKey: 'dismiss', emphasis: 'quiet' },
  ],
  needs_fix: [
    { to: 'fixed', labelKey: 'markFixed', emphasis: 'primary' },
    { to: 'dismissed', labelKey: 'dismiss', emphasis: 'quiet' },
  ],
  fixed: [
    { to: 'needs_fix', labelKey: 'reopen', emphasis: 'primary' },
    { to: 'dismissed', labelKey: 'dismiss', emphasis: 'quiet' },
  ],
  dismissed: [{ to: 'new', labelKey: 'restore', emphasis: 'primary' }],
};

export function actionsFor(status: ReportStatus): readonly ReportAction[] {
  return ACTIONS[status];
}
